import { Box, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import BackButton from "components/common/BackButton";
import ErrorPage from "components/common/ErrorPage";
import Feedback from "components/common/Feedback";
import { HandleApiFailure } from "components/common/HandleApiFailure";
import Snackbar from "components/common/Snackbar";
import AboveProperty from "components/features/AboveProperty";
import { fetchBookmarkPropertyOrPlan, fetchPropertyInfo } from "components/features/AboveProperty/AboveProperty.slice";
import IndividualProperty from "components/features/AboveProperty/IndividualProperty";
import PlanProgress from "components/features/AboveProperty/PlanView/PlansProgress";
import UserView from "components/features/AboveProperty/UserView";
import Dashboard from "components/features/dashboard";
import PreferredEmailDialog from "components/features/dashboard/PreferredEmailDialog";
import ImpersonateBanner from "components/features/ImpersonateUser/Banner";
import InncodeUsers from "components/features/InncodeUsers/home";
import Library from "components/features/library";
import { fetchBookmarkPlansIdAsync } from "components/features/library/Bookmark/Bookmark.slice";
import FunctionalArea from "components/features/library/functionalArea";
import { fetchFunctionalAreaAsync } from "components/features/library/libraryHall/Hall.slice";
import LibraryHallSearch from "components/features/library/Search";
import MyPlanDetails from "components/features/MyPlanDetails/index";
import MyPlans from "components/features/MyPlans";
import { fetchBookmarkedInncodeAsync, setInncodeDetails } from "components/features/MyPlans/myplans.slice";
import NavBar from "components/features/Navbar";
import { getNotifications } from "components/features/Notifications/Notifications.slice";
import Onboarding from "components/features/Onboarding";
import {
  fetchOnboardingQueries,
  fetchOnboardingQueriesCheck,
  setShowOnboardingWizard
} from "components/features/Onboarding/Onboarding.slice";
import PlanDetails from "components/features/planDetails";
import Recommendations from "components/features/Recommendations";
import { getRecommendationAccess } from "components/features/Recommendations/Recommendations.slice";
import SessionTimeout from "components/features/SessionTimeout";
import SMEHome from "components/features/SME/home";
import InncodeStats from "components/features/SME/inncodeStats";
import PlanStats from "components/features/SME/planStats";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { nestedTernaryCheckerCond1 } from "utils/CommonChecker";
import {
  abovePropertyViewTypePlanTemplate,
  ABOVE_PROP_USER_TYPE,
  DEFAULT_CORP_INNCODE,
  messages,
  RETRIEVE_NOTIFICATION_EVENT_TYPE,
  staticInncodesEnv
} from "utils/CommonConstants";
import { decrypt, getHltInncodes, getIsCorpUser, getLoggedInUserHltId } from "utils/CommonFunctions";
import { routes } from "utils/routes";
import { ON_BOARDING } from "utils/URLConstants";
import { theme as MUITheme } from "../../styles/Theme";
import {
  fetchAllowedRoles,
  fetchLoggedInUserInfoAsync,
  fetchRoles,
  fetchSMEUser,
  setAbovePropertyUser,
  setAdmin,
  setInnCode,
  setLoadPages,
  setLobbyUser,
  submitUserLoggedInDate
} from "./App.slice";
import ThemeContext from "./Context";
import DialogsContextLayout from "./DialogsContextLayout";
import Loader from "./Loader";
import "./Main.scss";

const Main = () => {
  const dispatch: any = useAppDispatch();
  const location = useLocation();
  const hltId = getLoggedInUserHltId();
  const isMobileScreen = useMediaQuery(MUITheme.breakpoints.down("md"));
  const contextValue = useMemo(() => ({ isMobileScreen }), [isMobileScreen]);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);

  const onboardingDetails: any = useAppSelector((state) => state.onboardingResponse.onboardingResponse);
  const isOnboardingCompleted: any = useAppSelector((state) => state.onboardingResponse.isOnboardingCompleted);
  const showOnboardingWizard: any = useAppSelector((state) => state.onboardingResponse.showOnboardingWizard);
  const showOnboardingWizardFromLibrary: any = useAppSelector(
    (state) => state.onboardingResponse.showOnboardingWizardFromLibrary
  );

  const innCodeData = useAppSelector((state: any) => state.app.innCode);
  const isAdmin = useAppSelector((state) => state.app.isAdmin);
  const loadPages = useAppSelector((state) => state.app.loadPages);
  const isSmeUser: any = useAppSelector((state) => state.app.isSmeUser);
  const isSmeUserFetched: any = useAppSelector((state) => state.app.isSmeUserFetched);
  const isAboveProperty = useAppSelector((state) => state.app.isAbovePropertyUser);
  const isAbovePropertyUserFetched: any = useAppSelector((state) => state.app.isAbovePropertyUserFetched);
  const innCodesData = useAppSelector((state: any) => state.app.innCodes);
  const isImpersonated = useAppSelector((state: any) => state.app.impersonationDetails.isImpersonated);
  const isAbovePropertyUser = isAbovePropertyUserFetched ? isAboveProperty : true;

  const adminInncodes = useAppSelector((state) => state.myplans.MyPlans.adminInncodes);

  const bookmarkedIdList = useAppSelector((state) => state.aboveProperty?.bookmarkedIdList);
  const bookmarkedTemplate = useAppSelector((state) => state.aboveProperty?.bookmarkedTemplate);

  const innCode: any = localStorage.getItem("defaultInncode");
  const innCodeValue = localStorage.getItem("innCode") ? localStorage.getItem("innCode") : innCode;
  const lobbyUserType = decrypt("salt", localStorage.getItem("lobbyUserType"));

  const isDashboard = location.pathname.split("/")[1] === "";
  const visitor = { id: isImpersonated ? `impersonate${hltId}` : hltId };

  useEffect(() => {
    if (isAdmin) dispatch(setInnCode(DEFAULT_CORP_INNCODE));
  }, [isAdmin]);

  useEffect(() => {
    const isAbovePropertyUser = String(lobbyUserType).toLowerCase() === ABOVE_PROP_USER_TYPE;
    dispatch(setInnCode(innCodeValue));
    dispatch(setLobbyUser(lobbyUserType));
    dispatch(setAbovePropertyUser(isAbovePropertyUser));
    dispatch(fetchLoggedInUserInfoAsync({ size: 1, value: hltId }));
    dispatch(fetchRoles({}));
    if (isEmpty(functionalAreaList)) dispatch(fetchFunctionalAreaAsync({}));
    dispatch(submitUserLoggedInDate({}));
  }, []);

  useEffect(() => {
    dispatch(fetchBookmarkPlansIdAsync());
    if (isEmpty(bookmarkedIdList) && isEmpty(bookmarkedTemplate) && isAbovePropertyUser)
      dispatch(fetchBookmarkPropertyOrPlan({ viewType: abovePropertyViewTypePlanTemplate }));
    dispatch(
      getNotifications({
        size: 30,
        eventType: RETRIEVE_NOTIFICATION_EVENT_TYPE
      })
    );
  }, [isImpersonated, isAbovePropertyUser]);

  useEffect(() => {
    if (!isAdmin && !isAbovePropertyUser) {
      dispatch(fetchOnboardingQueriesCheck({}));
    }
  }, [isImpersonated, isAdmin, isAbovePropertyUser]);

  useEffect(() => {
    if (!isAdmin && !isAbovePropertyUser) {
      if (!isOnboardingCompleted && isEmpty(onboardingDetails) && !isImpersonated) {
        dispatch(
          fetchOnboardingQueries({
            wizardType: ON_BOARDING
          })
        );
      }

      const showWizard = isImpersonated
        ? showOnboardingWizardFromLibrary
        : !isOnboardingCompleted || showOnboardingWizardFromLibrary;

      if (showWizard && !isEmpty(onboardingDetails)) {
        dispatch(setShowOnboardingWizard(true));
        dispatch(setLoadPages(false));
      } else {
        dispatch(setShowOnboardingWizard(false));
        dispatch(setLoadPages(true));
      }
    } else {
      dispatch(setShowOnboardingWizard(false));
      dispatch(setLoadPages(true));
    }
  }, [
    isAdmin,
    isAbovePropertyUser,
    onboardingDetails,
    isOnboardingCompleted,
    isImpersonated,
    showOnboardingWizardFromLibrary
  ]);

  useEffect(() => {
    if (window.pendo) {
      window.pendo.initialize({
        visitor,
        account: {
          id: innCodeData
        }
      });
    }

    if (isAbovePropertyUserFetched && isAbovePropertyUser) {
      const inncodes =
        "ABEHW,ABITX,ABQAA,ABQAP,ABQCO,ABQMT,ABZDD,ABZWE,ACTOR,ACYHW,AGSAW,ALLHW,ALTDW,AMADO,AMATB,ANRCS,ARLAH,ASBNC,ATLAM,ATLDW,ATLES,ATLMI,ATLMN,ATLPH,AUSDT,AUSIM,AUSUN,AZOKP,BALAM,BALHE,BALIA,BALWM,BHMEA,BHXBS,BJXUP,BLKPL,BNACC,BNAUV,BNAVN,BOIDO,BOSAP,BOSBW,BOSNS,BOSWA,BTLMI,BTRCP,BTTVT,BWIWM,CAEHW,CAKAP,CAKFA,CAKOH,CHIGU,CHIJB,CHILM,CHILO,CHIMW,CHIOB,CHITP,CHIWJ,CHKSF,CLEIN,CLETH,CLLGI,CLLHT,CLTCM,CLTCU,CLTTG,CMHES,CMHHI,CRPKV,CRPSA,CRSTX,CUUMX,CVGCV,CVGDL,CVGSK,DALAG,DALAL,DALAN,DALAS,DALBE,DALDL,DALDR,DALDX,DALGV,DALIG,DALLC,DALLF,DALMA,DALNY,DALPO,DALTX,DAYSO,DCACC,DCAVA,DCUAT,DENCC,DENCP,DENCV,DENDC,DENGW,DENUS,DENWM,DFWCN,DFWFS,DFWHA,DFWLT,DFWRM,DFWSC,DFWSH,DFWST,DNDHX,DSASH,EDIFB,ELPGI,EMVWE,EWRPP,EWRRC,EYWDT,FDRMD,FDYOH,FMYHS,FTWAA,FTWRR,FWBHH,GGGGI,GLAHA,GRRMI,GWSCO,GYYMR,HLGWH,HMORS,HOUBE,HOUHW,HOUIC,HOUMC,HOUPO,HOURS,HOUST,HOUWD,HOUWI,HOUWM,HOUWT,HSBSO,HSBUV,HSVSO,IAHCC,ICTEA,ILGDT,ILMMT,INDST,INDUP,ISNNR,ITOHN,JAXJV,JFKML,KNXKH,LANDT,LASAP,LASRU,LASSM,LAWTN,LAXDT,LAXHO,LAXMA,LAXNW,LBBTX,LBBUA,LGBSP,LHRAT,LION,LITDO,LITDT,LITMM,LITNL,LKMFL,MAFDC,MAFTW,MCOUN,MDWAH,MEMHW,MEMPE,MEMSG,MEMSW,MFDON,MIACA,MIADA,MIASB,MIFGI,MKEBK,MLBSK,MLVNY,MMEKT,MSNBJ,MSNLD,MSPBV,MSPCU,MSPEM,MSPLO,MSPMD,MSPVI,MSYDT,MSYES,MSYFQ,MSYGD,MSYGI,MSYKN,MSYLA,MSYPY,MTYGA,NCLHX,NYCFD,NYCTF,OAKPT,OGGWM,OKCOK,OLMCW,OLVCL,ONTGI,ONTPO,ORDSI,ORHWO,ORYOR,PASPH,PBIBR,PBICA,PBICT,PBIPB,PDXBV,PDXPT,PDXSH,PDXTG,PHXHV,PHXTC,PHXTP,PVDWR,QROHT,RCSHX,REXHA,RKFIL,RNOGI,SANDM,SANHC,SANMZ,SANSW,SATAI,SATBO,SATHW,SEALX,SFOFD,SFOHS,SHNOK,SJCGH,SJCHM,SLCAR,SLCHW,SLWHA,SMFDA,SMOCA,SNACA,SNACS,SPSSS,SRQDT,SRQFL,SRQHT,SRQNB,STJMO,STLCU,STLDS,STLGA,STNAR,STSWI,STVPT,SVHHH,SVOHF,SYRDE,TASTC,TOLME,TPAHS,TPAHW,TPANO,TRCHA,TULAH,TUSES,TUSHI,TUSHT,TUSSP,TYRTS,VALES,VERBR,VOGGI,WASRR,YNGOH,YTOPC,ZZVOH";
      if (staticInncodesEnv.includes(process.env.REACT_APP_ACTIVE_ENV || ""))
        localStorage.setItem("hltInnCodes", inncodes);
      const innCodesValue = staticInncodesEnv.includes(process.env.REACT_APP_ACTIVE_ENV || "")
        ? inncodes
        : getHltInncodes(true);
      dispatch(fetchPropertyInfo({ innCodesValue }));
    }
  }, [isAbovePropertyUserFetched, isAbovePropertyUser]);

  useEffect(() => {
    if (window.pendo) {
      window.pendo.updateOptions({
        visitor,
        account: {
          id: innCodeData
        }
      });
    }
    if (isAdmin) {
      let tempInncodes: any = innCodesData.length ? innCodesData : adminInncodes;
      tempInncodes = tempInncodes.filter((item: any) => item.innCode === innCodeData);
      if (tempInncodes?.length) dispatch(setInncodeDetails(tempInncodes));
    }
  }, [innCodeData]);

  useEffect(() => {
    if (getIsCorpUser(lobbyUserType)) {
      dispatch(setAdmin(true));
      dispatch(getRecommendationAccess({}));
      dispatch(fetchBookmarkedInncodeAsync({}));
      dispatch(fetchSMEUser({}));
      dispatch(fetchAllowedRoles({}));
    } else {
      dispatch(setAdmin(false));
    }
  }, [lobbyUserType]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={MUITheme}>
        <div className="App">
          {showOnboardingWizard && !loadPages && <Onboarding />}
          <Box>
            <CssBaseline />
            {isImpersonated && <ImpersonateBanner />}
            <NavBar />
            <Feedback form="apiFailure" />
            <HandleApiFailure />
            <PreferredEmailDialog />

            {!showOnboardingWizard && loadPages && (!isAdmin || isSmeUserFetched) && (
              <ErrorBoundary fallback={<ErrorPage />}>
                <Box
                  component="main"
                  className={isImpersonated ? "impersonate-main" : "non-impersonated-main"}
                  marginLeft={{ md: "137px", xs: "16px" }}
                  marginRight={{
                    md: nestedTernaryCheckerCond1(isDashboard, !isSmeUser && isAdmin, "32px", "0", "32px"),
                    xs: "16px"
                  }}
                  paddingY={{ xs: "16px", md: "unset" }}
                  sx={{
                    borderTop:
                      location.pathname.split("/")[1] !== "library" && !isMobileScreen
                        ? `1px solid ${tokens.Border.General.Tertiary}`
                        : "none"
                  }}
                >
                  <Snackbar />
                  <Feedback form="updatePlan" message={messages.editSuccess} />
                  {isMobileScreen && <BackButton label="Back" />}
                  <Routes>
                    <Route element={<DialogsContextLayout />}>
                      {isAdmin && isSmeUser && (
                        <>
                          <Route path={routes.Dashboard} element={<SMEHome />} />
                          <Route path={routes.PlanStats} element={<PlanStats />} />
                          <Route path={routes.InnCodeStats} element={<InncodeStats />} />
                          <Route path={routes.Dashboard} element={<Dashboard />} />
                          <Route path={routes.Library} element={<Library />} />
                        </>
                      )}
                      {isAdmin && !isSmeUser && (
                        <>
                          <Route index path={routes.Dashboard} element={<Library />} />
                        </>
                      )}
                      {!isAdmin && (
                        <>
                          {isAbovePropertyUser ? (
                            <>
                              <Route path={routes.UserView} element={<UserView />} />
                              <Route path={routes.Dashboard} element={<AboveProperty />} />
                              <Route path={routes.IndividualProperty} element={<IndividualProperty />} />
                              <Route path={routes.UserDashboard} element={<Dashboard />} />
                            </>
                          ) : (
                            <Route path={routes.Dashboard} element={<Dashboard />} />
                          )}
                          <Route path={routes.Library} element={<Library />} />
                        </>
                      )}
                      {isAdmin && (
                        <>
                          <Route path={routes.InncodeUsers} element={<InncodeUsers />} />
                          <Route path={routes.UserDashboard} element={<Dashboard />} />
                        </>
                      )}
                      <Route path={routes.MyPlans} element={<MyPlans />} />
                      <Route path={routes.Recommendations} element={<Recommendations />} />

                      <Route path={routes.FunctionalArea} element={<FunctionalArea />} />
                      <Route path={routes.MyPlanDetails} element={<MyPlanDetails />}>
                        <Route path={routes.sectionDetails} element={<MyPlanDetails />}>
                          <Route path={routes.CardDetails} element={<MyPlanDetails />} />
                        </Route>
                      </Route>
                      <Route path={routes.LibraryHallSearch} element={<LibraryHallSearch />} />
                      <Route path={routes.PlanDetails} element={<PlanDetails />}>
                        <Route path={routes.sectionDetails} element={<PlanDetails />}>
                          <Route path={routes.CardDetails} element={<PlanDetails />} />
                        </Route>
                      </Route>
                      <Route path={`${routes.PlanProgress}/:id`} element={<PlanProgress />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Box>
              </ErrorBoundary>
            )}
          </Box>
        </div>
        <SessionTimeout />
        <Loader />
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default Main;
